/**
 * Ad and campaign attribution, captured from the landing URL.
 *
 * Google Ads appends `gclid` (or `gbraid`/`wbraid` on iOS) to every click, and our
 * ads add `utm_*` tags. None of that survives the trip through signup on its own, so
 * it's stored here on landing and sent along when someone signs up, starts a roast or
 * checks out. The server keeps it in attribution_touches, so a sale on another device
 * still credits the visit that found us.
 *
 * Two touches are kept:
 * - Last touch: the latest non-direct visit (tags, a partner link or another site's
 *   referral) replaces an earlier one; a direct visit leaves it alone. Entries expire
 *   after 90 days, Google Ads' longest click window.
 * - First touch: written once and never replaced, so "how did they first find us"
 *   survives every later visit.
 */

const STORAGE_KEY = 'twigAttribution';
const FIRST_TOUCH_KEY = 'twigFirstTouch';
const PARAMS = [
  'gclid',
  'gbraid',
  'wbraid',
  'fbclid',
  'msclkid',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
];
const MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000;

// Hops that aren't a source: our own sites, checkout and sign-in round trips
const INTERNAL_REFERRERS = [
  /(^|\.)twig\.io$/,
  /(^|\.)stripe\.com$/,
  /(^|\.)firebaseapp\.com$/,
  /^accounts\.google\.com$/,
];

export type Attribution = Record<string, string>;

interface StoredAttribution {
  params: Attribution;
  capturedAt: number;
  via?: string;
  referrerHost?: string;
  landingPath?: string;
}

/** The shape the server's sanitizeTouch() accepts. */
export interface Touch {
  params: Attribution | null;
  via: string | null;
  referrerHost: string | null;
  landingPath: string | null;
  capturedAt: number;
}

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

/**
 * Drop our own internal tags. Until the popup fix (#50) the roast popup linked with
 * utm_source=twig, and that says nothing about where a visitor came from, so it must never
 * be stored or replace a real source. Click IDs are kept either way.
 */
function withoutInternalTags(params: Attribution): Attribution {
  if (params['utm_source']?.toLowerCase() !== 'twig') return params;
  return Object.fromEntries(Object.entries(params).filter(([key]) => !key.startsWith('utm_')));
}

export function parseAttribution(search: string): Attribution | null {
  const query = new URLSearchParams(search);
  const params: Attribution = {};
  for (const key of PARAMS) {
    const value = query.get(key)?.trim();
    if (value) params[key] = value;
  }
  const kept = withoutInternalTags(params);
  return Object.keys(kept).length ? kept : null;
}

/** The referring site's host, or null for a direct visit or one of our own hops. */
export function externalReferrerHost(referrer: string): string | null {
  if (!referrer) return null;
  try {
    const host = new URL(referrer).hostname.toLowerCase();
    return INTERNAL_REFERRERS.some(pattern => pattern.test(host)) ? null : host;
  } catch {
    return null;
  }
}

function defaultStorage(): StorageLike | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    // Private mode and blocked storage throw on access
    return null;
  }
}

function read(storage: StorageLike, key: string): StoredAttribution | null {
  try {
    const raw = storage.getItem(key);
    if (!raw) return null;
    const stored = JSON.parse(raw) as StoredAttribution;
    if (!stored || typeof stored.capturedAt !== 'number') return null;
    // A touch stored before the popup fix: forget its internal tags, and the touch itself if
    // that was all it had, so the visitor's real source can take its place
    const params = withoutInternalTags(stored.params ?? {});
    if (!Object.keys(params).length && !stored.via && !stored.referrerHost) return null;
    return { ...stored, params };
  } catch {
    return null;
  }
}

export function captureAttribution(
  search: string = globalThis.location?.search ?? '',
  storage: StorageLike | null = defaultStorage(),
  now: number = Date.now(),
  referrer: string = globalThis.document?.referrer ?? '',
  landingPath: string = globalThis.location?.pathname ?? ''
): void {
  if (!storage) return;
  const params = parseAttribution(search);
  const via = new URLSearchParams(search).get('via')?.trim().toLowerCase() || undefined;
  const referrerHost = externalReferrerHost(referrer) ?? undefined;
  // A direct visit says nothing about where someone came from, so it changes nothing
  if (!params && !via && !referrerHost) return;

  const touch: StoredAttribution = {
    params: params ?? {},
    capturedAt: now,
    ...(via && { via }),
    ...(referrerHost && { referrerHost }),
    ...(landingPath && { landingPath }),
  };
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(touch));
    if (!read(storage, FIRST_TOUCH_KEY)) storage.setItem(FIRST_TOUCH_KEY, JSON.stringify(touch));
  } catch {
    // Attribution is best-effort; a full or blocked storage must not break the page
  }
}

/** The last touch's tags, for the Customer.io traits and Stripe metadata. */
export function getAttribution(
  storage: StorageLike | null = defaultStorage(),
  now: number = Date.now()
): Attribution | undefined {
  if (!storage) return undefined;
  const stored = read(storage, STORAGE_KEY);
  if (!stored) return undefined;
  if (!stored.params || now - stored.capturedAt > MAX_AGE_MS) {
    try {
      storage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
    return undefined;
  }
  return Object.keys(stored.params).length ? stored.params : undefined;
}

function toTouch(stored: StoredAttribution | null): Touch | undefined {
  if (!stored) return undefined;
  return {
    params: stored.params && Object.keys(stored.params).length ? stored.params : null,
    via: stored.via ?? null,
    referrerHost: stored.referrerHost ?? null,
    landingPath: stored.landingPath ?? null,
    capturedAt: stored.capturedAt,
  };
}

/** First and last touch for the server, sent with signup, roast start and checkout. */
export function getTouches(
  storage: StorageLike | null = defaultStorage(),
  now: number = Date.now()
): { first?: Touch; last?: Touch } | undefined {
  if (!storage) return undefined;
  getAttribution(storage, now); // expires a stale last touch
  const first = toTouch(read(storage, FIRST_TOUCH_KEY));
  const last = toTouch(read(storage, STORAGE_KEY));
  if (!first && !last) return undefined;
  return { ...(first && { first }), ...(last && { last }) };
}

/**
 * The note twig.io's forwarder leaves (the `twig_land` cookie, 30 minutes) when a visitor
 * arrived there from another site. In the app their referrer is twig.io, so this is the
 * only record of where they really came from.
 */
export function readLandingNote(
  cookies: string = globalThis.document?.cookie ?? '',
  now: number = Date.now()
): { referrer: string; landingPath: string } | null {
  const match = cookies.match(/(?:^|;\s*)twig_land=([^;]+)/);
  if (!match?.[1]) return null;
  try {
    const note = JSON.parse(decodeURIComponent(match[1])) as { r?: unknown; p?: unknown; t?: unknown };
    if (typeof note.r !== 'string' || typeof note.t !== 'number' || now - note.t > 30 * 60 * 1000) return null;
    return { referrer: `https://${note.r}/`, landingPath: typeof note.p === 'string' ? note.p : '' };
  } catch {
    return null;
  }
}

/** Send the touches to the server, which keeps a copy in an HttpOnly cookie. Fire and forget. */
export function syncTouchesToServer(): void {
  const touches = getTouches();
  if (!touches) return;
  fetch('/api/attr', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ touches }),
  }).catch(() => {
    // Best-effort: the browser's own copy still goes with signup and checkout
  });
}
